/**
 * Cross-program campaign management surface, including filtering, creation,
 * selection, and bulk actions above the campaign data table.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@amzn/meridian/data-table';
import { type OnSort, type SortDirection } from '@amzn/meridian/data-table/types/column-sort';
import Heading from '@amzn/meridian/heading';
import Loader from '@amzn/meridian/loader';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { BulkEditModal, type BulkEditMode } from 'src/components/campaigns/BulkEditModal';
import { type CampaignFilterState } from 'src/components/common/CampaignFilterBar';
import { CampaignListActionBar } from 'src/components/campaigns/CampaignListActionBar';
import { FormCampaignEdit } from 'src/components/campaigns/forms/FormCampaignEdit';
import { PageLoader } from 'src/components/common/loaders/page-loader';
import { ShowingCount } from 'src/components/common/data-table-action-bar/ShowingCount';
import { TablePagination } from 'src/components/common/data-table-action-bar/TablePagination';
import { ConfigurationType, CreationSource, DEFAULT_REFRESH_SCHEDULE_CRON, FEATURE_FLAGS } from 'src/constants';
import { isFeatureEnabled } from 'src/feature-flags';
import { useDebounce } from 'src/hooks/useDebounce';
import { CampaignStatuses } from 'src/store/constants';
import { type CampaignDesiredStateDto } from 'src/store/generated/leela-api-generated';
import {
  useAssetsControllerGetDistinctCampaignOptionsQuery,
  useCampaignConfigurationControllerFindManyQuery,
  useCampaignConfigurationControllerGetQuery,
  useCampaignsControllerFindManyQuery,
  useProgramsControllerCreateMutation,
} from 'src/store/leela-api';
import { type ClonedCampaignData } from 'src/types/form.types';
import { buildClonedCampaignData } from 'src/utils/cloneCampaign';
import { CampaignTableRow } from 'src/components/campaigns/CampaignTableRow';

interface CampaignOption {
  productStrategies?: unknown[];
}

interface ActiveFilterTag {
  key: string;
  label: string;
  testId: string;
}

const deriveBusinessLines = (campaigns: CampaignOption[] | undefined): string[] => {
  if (!campaigns) return [];
  const businessLines = new Set<string>();
  campaigns.forEach((campaign) => {
    ((campaign.productStrategies ?? []) as Array<{ businessLine?: string }>).forEach((strategy) => {
      if (strategy.businessLine) businessLines.add(strategy.businessLine);
    });
  });
  return Array.from(businessLines).sort();
};

export const CampaignList: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const configId = searchParams.get('configId') ?? undefined;
  const publisher = searchParams.get('publisher') ?? '';
  const status = searchParams.get('status') ?? '';
  const countries = useMemo(() => {
    const raw = searchParams.get('countries') ?? '';
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [searchParams]);
  const businessLine = searchParams.get('businessLine') ?? '';
  const urlSearch = searchParams.get('search') ?? '';
  const [localSearch, setLocalSearch] = useState(urlSearch);
  const [debouncedSearch] = useDebounce(localSearch, 300) as [string, boolean];

  const setFilterParam = useCallback((key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const campaignFilters: CampaignFilterState = useMemo(() => ({
    publisher,
    status,
    countries,
    businessLine,
  }), [publisher, status, countries, businessLine]);

  const { data: blocklistConfig } = useCampaignConfigurationControllerGetQuery(
    { id: configId! },
    { skip: !configId },
  );

  const clearConfigIdFilter = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('configId');
      return next;
    }, { replace: true });
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [setSearchParams]);

  // Clone modal state
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [cloneData, setCloneData] = useState<ClonedCampaignData | undefined>();

  // Create campaign modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createProgram] = useProgramsControllerCreateMutation();

  // Blocklist dropdown search state
  const [blocklistSearch, setBlocklistSearch] = useState('');
  const [debouncedBlocklistSearch] = useDebounce(blocklistSearch, 300) as [string, boolean];

  const { data: blocklistConfigs, isFetching: isBlocklistFetching } = useCampaignConfigurationControllerFindManyQuery({
    configType: ConfigurationType.GTI_BLOCKLIST,
    source: CreationSource.USER,
    includeCampaignCount: false,
    pageSize: 20,
    page: 1,
    sort: ['updatedAt:-1'],
    ...(debouncedBlocklistSearch && { search: debouncedBlocklistSearch }),
  }, {
    skip: !isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST) || (!isCloneModalOpen && !isCreateModalOpen),
  });

  const handleClone = (campaign: CampaignDesiredStateDto) => {
    setCloneData(buildClonedCampaignData(campaign));
    setIsCloneModalOpen(true);
  };

  const handleCloneModalCancel = () => {
    setIsCloneModalOpen(false);
    setCloneData(undefined);
  };

  const handleCloneModalSave = () => {
    setIsCloneModalOpen(false);
    setCloneData(undefined);
  };

  const handleCreateCampaign = () => {
    setIsCreateModalOpen(true);
  };

  const handleBeforeCreate = async (programFields: { schedule?: unknown; userSharedList?: string[]; campaignName?: string; country?: string }): Promise<string> => {
    const datePart = new Date().toISOString().split('T')[0];
    const nameParts = [programFields.campaignName, programFields.country, datePart].filter(Boolean);
    const programName = nameParts.join('_') || `Campaign Program - ${datePart}`;

    const response = await createProgram({
      createProgramDto: {
        name: programName,
        countries: { US: ['en_US'] },
        schedule: (programFields.schedule as { refreshScheduleCron: string }) ?? { refreshScheduleCron: DEFAULT_REFRESH_SCHEDULE_CRON },
        status: 'ACTIVE',
        gtiBlocklist: [],
        userSharedList: programFields.userSharedList ?? [],
      },
    });

    if ('data' in response) {
      return response.data.programId;
    }
    throw new Error('Failed to create program');
  };

  const handleCreateModalCancel = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateModalSave = () => {
    setIsCreateModalOpen(false);
  };

  // Selection state
  const [selectedCampaigns, setSelectedCampaigns] = useState<Map<string, CampaignDesiredStateDto>>(new Map());
  const [bulkEditMode, setBulkEditMode] = useState<BulkEditMode | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    pageCount: 1,
  });

  // Reset pagination synchronously when configId changes via navigation (back/forward, different blocklist link)
  const prevConfigId = React.useRef(configId);
  if (prevConfigId.current !== configId) {
    prevConfigId.current = configId;
    setPagination((prev) => ({ ...prev, page: 1 }));
  }

  // Sort state
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] =
    useState<SortDirection>('descending');

  const sort = useMemo(
    () => [`${sortColumn}:${sortDirection === 'ascending' ? 1 : -1}`],
    [sortColumn, sortDirection],
  );

  const onSort: OnSort = useCallback(({
    sortColumn,
    sortDirection,
  }) => {
    if (sortDirection !== 'none') {
      setPagination((prev) => ({ ...prev, page: 1 }));
      setSortColumn(sortColumn);
      setSortDirection(sortDirection);
    }
  }, []);

  const { data: allCampaignOptions } = useAssetsControllerGetDistinctCampaignOptionsQuery();

  const publisherOptions = useMemo(() => {
    if (!allCampaignOptions) return [];
    const publishers = new Set<string>();
    allCampaignOptions.forEach((campaign) => {
      if (campaign.publisher) publishers.add(campaign.publisher);
    });
    return Array.from(publishers).sort();
  }, [allCampaignOptions]);

  const businessLineOptions = useMemo(
    () => deriveBusinessLines(allCampaignOptions),
    [allCampaignOptions],
  );

  const handleFiltersChange = useCallback((newFilters: CampaignFilterState) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newFilters.publisher) next.set('publisher', newFilters.publisher);
      else next.delete('publisher');
      if (newFilters.status) next.set('status', newFilters.status);
      else next.delete('status');
      if (newFilters.countries.length > 0) next.set('countries', newFilters.countries.join(','));
      else next.delete('countries');
      if (newFilters.businessLine) next.set('businessLine', newFilters.businessLine);
      else next.delete('businessLine');
      return next;
    }, { replace: true });
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [setSearchParams]);

  useEffect(() => {
    if (debouncedSearch !== urlSearch) {
      setFilterParam('search', debouncedSearch);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [debouncedSearch, setFilterParam, urlSearch]);

  useEffect(() => {
    setLocalSearch(urlSearch);
  }, [urlSearch]);

  const handleSearchSubmit = useCallback(() => {
    setFilterParam('search', localSearch);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [localSearch, setFilterParam]);

  const activeFilterTags = useMemo(() => {
    const tags: ActiveFilterTag[] = [];
    if (campaignFilters.publisher) {
      tags.push({
        key: 'publisher',
        label: `${t('publisher')}: ${campaignFilters.publisher}`,
        testId: 'filter-tag-publisher',
      });
    }
    if (campaignFilters.status) {
      tags.push({
        key: 'status',
        label: `${t('form_global_status')}: ${campaignFilters.status}`,
        testId: 'filter-tag-status',
      });
    }
    campaignFilters.countries.forEach((country) => {
      tags.push({
        key: `country:${country}`,
        label: `${t('country')}: ${country}`,
        testId: `filter-tag-country-${country}`,
      });
    });
    if (campaignFilters.businessLine) {
      tags.push({
        key: 'businessLine',
        label: `${t('business_line')}: ${campaignFilters.businessLine}`,
        testId: 'filter-tag-businessline',
      });
    }
    return tags;
  }, [campaignFilters, t]);

  const hasActiveFilters = activeFilterTags.length > 0 || localSearch !== '';

  const handleRemoveFilterTag = useCallback((key: string) => {
    if (key.startsWith('country:')) {
      const country = key.slice('country:'.length);
      handleFiltersChange({
        ...campaignFilters,
        countries: campaignFilters.countries.filter((value) => value !== country),
      });
      return;
    }
    handleFiltersChange({ ...campaignFilters, [key]: '' });
  }, [campaignFilters, handleFiltersChange]);

  const handleClearAllFilters = useCallback(() => {
    setLocalSearch('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('publisher');
      next.delete('status');
      next.delete('countries');
      next.delete('businessLine');
      next.delete('search');
      return next;
    }, { replace: true });
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [setSearchParams]);

  // Build API filters object from URL-persisted campaign filters and debounced search
  const apiFilters = useMemo(() => {
    const obj: Record<string, unknown> = {};
    if (campaignFilters.publisher) obj.publisher = campaignFilters.publisher;
    if (campaignFilters.status) obj.status = campaignFilters.status;
    if (debouncedSearch) obj.campaignName = debouncedSearch;
    if (campaignFilters.countries.length > 0) obj.country = campaignFilters.countries;
    if (campaignFilters.businessLine) obj.businessLine = campaignFilters.businessLine;
    return obj;
  }, [campaignFilters, debouncedSearch]);

  // Merge URL-driven configId filter with filter bar filters
  const mergedFilters = useMemo(() => {
    if (!configId) return apiFilters;
    return { ...apiFilters, configId };
  }, [apiFilters, configId]);

  // Note: isFetching is used for the linear loading indicator but NOT for clearing the table.
  // Showing a spinner on re-fetch clears the table and visually resets bulk selection state.
  const { data, isLoading, isFetching } = useCampaignsControllerFindManyQuery({
    page: pagination.page,
    pageSize: pagination.pageSize,
    sort,
    filters: mergedFilters,
    ...(campaignFilters.status === CampaignStatuses.ARCHIVED && { includeArchived: true }),
  });

  const paginationResponse = data?.meta?.pagination;
  const campaigns = useMemo<CampaignDesiredStateDto[]>(() => data?.data ?? [], [data?.data]);

  const handleToggleSelect = useCallback((campaignId: string) => {
    setSelectedCampaigns((prev) => {
      const next = new Map(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        const campaign = campaigns.find((c) => c.campaignId === campaignId);
        if (campaign) {
          next.set(campaignId, campaign);
        }
      }
      return next;
    });
  }, [campaigns]);

  const showBulkControls = selectedCampaigns.size > 0;
  const columnCount = 7;
  const allOnPageSelected = campaigns.length > 0 && campaigns.every((c) => selectedCampaigns.has(c.campaignId));
  const someOnPageSelected = campaigns.some((c) => selectedCampaigns.has(c.campaignId));
  const selectAllState = allOnPageSelected ? true : someOnPageSelected ? 'mixed' as const : false;

  const handleSelectAll = useCallback(() => {
    setSelectedCampaigns((prev) => {
      const next = new Map(prev);
      if (allOnPageSelected) {
        campaigns.forEach((c) => next.delete(c.campaignId));
      } else {
        campaigns.forEach((c) => next.set(c.campaignId, c));
      }
      return next;
    });
  }, [allOnPageSelected, campaigns]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      page: paginationResponse?.page ?? 1,
      pageCount: paginationResponse?.pageCount ?? 1,
    }));
  }, [paginationResponse]);

  return (
    <>
    <Column>
      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <Heading level={1} type="h500">{t('campaigns_list_title')}</Heading>
      </Row>

      <CampaignListActionBar
        activeFilterTags={activeFilterTags}
        businessLineOptions={businessLineOptions}
        campaignFilters={campaignFilters}
        hasActiveFilters={hasActiveFilters}
        localSearch={localSearch}
        publisherOptions={publisherOptions}
        selectedCount={selectedCampaigns.size}
        showBulkControls={showBulkControls}
        onBulkEditMode={setBulkEditMode}
        onClearAllFilters={handleClearAllFilters}
        onCreateCampaign={handleCreateCampaign}
        onFiltersChange={handleFiltersChange}
        onRemoveFilterTag={handleRemoveFilterTag}
        onSearchChange={setLocalSearch}
        onSearchSubmit={handleSearchSubmit}
      />

      {configId && (
        <Row alignmentVertical="center" spacing="200">
          <Text color="secondary" type="b200">{t('form_gti_blocklist_header')}:</Text>
          <Tag data-testid="blocklist-filter-tag" type="theme" onClose={clearConfigIdFilter}>
            {blocklistConfig?.name ?? configId}
          </Tag>
        </Row>
      )}

      {isLoading
        ? (
        <Column
          alignmentHorizontal="center"
          alignmentVertical="center"
          height="300px"
        >
          <PageLoader message={t('campaign_loading')} />
        </Column>
          )
        : (
        <>
          <Row alignmentVertical="center" spacing="300">
            <ShowingCount
              page={pagination.page}
              pageSize={pagination.pageSize}
              total={paginationResponse?.total ?? 0}
            />
            {isFetching && !isLoading && (
              <Loader size="small" type="linear" />
            )}
          </Row>
          <DataTable
            data-testid="campaigns-table"
            showBorder={true}
            showRowDividers={true}
            size="medium"
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            stickyHeaderRows={true}
            onSort={onSort}
          >
            <DataTableHead backgroundColor="secondary">
              <DataTableRow>
                <DataTableHeader width={48}>
                  <Checkbox
                    checked={selectAllState}
                    data-testid="campaign-select-all-checkbox"
                    onChange={handleSelectAll}
                  />
                </DataTableHeader>
                <DataTableHeader sortColumn="name" width={320}>{t('table_header_name')}</DataTableHeader>
                <DataTableHeader sortColumn="publisher" width={140}>{t('table_header_publisher')}</DataTableHeader>
                <DataTableHeader sortColumn="status" width={130}>{t('table_header_campaign_status')}</DataTableHeader>
                <DataTableHeader width={100}>{t('form_global_sync_status')}</DataTableHeader>
                <DataTableHeader sortColumn="createdAt" width={260}>{t('table_header_created_date')}</DataTableHeader>
                <DataTableHeader width={80}>{t('table_header_actions')}</DataTableHeader>
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {campaigns?.length === 0 && (
                <DataTableRow>
                  <DataTableCell colSpan={columnCount}>{t('table_no_results')}</DataTableCell>
                </DataTableRow>
              )}
              {campaigns?.map((campaign: CampaignDesiredStateDto) => (
                <CampaignTableRow
                  key={campaign.campaignId}
                  onClone={handleClone}
                  {...campaign}
                  isSelected={selectedCampaigns.has(campaign.campaignId)}
                  onToggleSelect={handleToggleSelect}
                />
              ))}
            </DataTableBody>
          </DataTable>

          <TablePagination
            currentPage={pagination.page}
            itemsPerPage={pagination.pageSize}
            pageCount={pagination.pageCount}
            onItemsPerPageChange={(pageSize) => {
              setPagination((prev) => ({ ...prev, page: 1, pageSize }));
            }}
            onPageChange={(newPage) => {
              setPagination((prev) => ({ ...prev, page: newPage }));
            }}
          />
        </>
          )}
      {bulkEditMode !== null && (
        <BulkEditModal
          campaigns={Array.from(selectedCampaigns.values())}
          mode={bulkEditMode}
          onClose={() => { setBulkEditMode(null); }}
          onSuccess={() => {
            setBulkEditMode(null);
            setSelectedCampaigns(new Map());
          }}
        />
      )}
    </Column>

      <Modal
        data-testid="clone-campaign-modal"
        open={isCloneModalOpen}
        title={t('form_campaigns_create')}
        width={1000}
      >
        {isCloneModalOpen && (
          <FormCampaignEdit
            blocklistConfigs={blocklistConfigs}
            blocklistSearch={blocklistSearch}
            initialData={cloneData}
            isBlocklistLoading={isBlocklistFetching}
            onBeforeCreate={handleBeforeCreate}
            onBlocklistSearch={setBlocklistSearch}
            onCancel={handleCloneModalCancel}
            onSave={handleCloneModalSave}
          />
        )}
      </Modal>

      <Modal
        data-testid="create-campaign-modal"
        open={isCreateModalOpen}
        title={t('form_campaigns_create')}
        width={1000}
      >
        {isCreateModalOpen && (
          <FormCampaignEdit
            blocklistConfigs={blocklistConfigs}
            blocklistSearch={blocklistSearch}
            isBlocklistLoading={isBlocklistFetching}
            onBeforeCreate={handleBeforeCreate}
            onBlocklistSearch={setBlocklistSearch}
            onCancel={handleCreateModalCancel}
            onSave={handleCreateModalSave}
          />
        )}
      </Modal>
    </>
  );
};
